import { QRGenerator } from './qrGenerator';

export interface CompositeConfig {
  templateWidth: number;
  templateHeight: number;
  qrCodeWidth: number;
  qrCodeHeight: number;
  qrCodeX: number;
  qrCodeY: number;
  serialTextX: number;
  serialTextY: number; // baseline position
  serialFontSize: number;
  serialFontFamily: string;
}

// Portrait configuration from LIG-133
export const PORTRAIT_CONFIG: CompositeConfig = {
  templateWidth: 1200,
  templateHeight: 2100,
  qrCodeWidth: 677,
  qrCodeHeight: 677,
  qrCodeX: 129,
  qrCodeY: 672,
  serialTextX: 416,
  serialTextY: 1075,
  serialFontSize: 21,
  serialFontFamily: 'Inter, Arial, sans-serif',
};

// Landscape configuration from LIG-133
export const LANDSCAPE_CONFIG: CompositeConfig = {
  templateWidth: 2100,
  templateHeight: 1200,
  qrCodeWidth: 565,
  qrCodeHeight: 565,
  qrCodeX: 145,
  qrCodeY: 435,
  serialTextX: 346,
  serialTextY: 773,
  serialFontSize: 17,
  serialFontFamily: 'Inter, Arial, sans-serif',
};

export interface CompositeResult {
  canvas: HTMLCanvasElement;
  dataUrl: string;
}

export class ImageCompositor {
  /**
   * Load Inter font if not already loaded
   */
  static async loadInterFont(): Promise<void> {
    // Check if Inter font is already loaded
    if (document.fonts.check('12px Inter')) {
      return;
    }

    try {
      // Try to load Inter font from Google Fonts
      const link = document.createElement('link');
      link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
      
      // Wait for font to load
      await document.fonts.load('21px Inter');
      await document.fonts.load('17px Inter');
    } catch (error) {
      console.warn('Failed to load Inter font, will use fallback fonts:', error);
    }
  }

  /**
   * Create template image from base64 data
   * Auto-detect format based on base64 header
   */
  static async createTemplateImage(base64Data: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (error) => {
        console.error('Failed to load template image:', error);
        console.log('Base64 data length:', base64Data.length);
        console.log('Base64 data preview:', base64Data.substring(0, 100));
        reject(new Error('Failed to load template image'));
      };
      
      // Auto-detect image format from base64 data
      let mimeType = 'image/png'; // default to PNG
      
      if (base64Data.startsWith('iVBORw0KGgo')) {
        // PNG file header
        mimeType = 'image/png';
      } else if (base64Data.startsWith('TU0AKg') || base64Data.startsWith('SUkq')) {
        // TIFF file headers (MM\x00\x2a or II*\x00 in base64)
        console.warn('TIFF format detected - browsers cannot display TIFF images directly');
        mimeType = 'image/tiff';
      } else if (base64Data.startsWith('/9j/')) {
        // JPEG file header
        mimeType = 'image/jpeg';
      }
      
      console.log(`Detected image format: ${mimeType}`);
      img.src = `data:${mimeType};base64,${base64Data}`;
    });
  }

  /**
   * Generate composite image for portrait orientation
   */
  static async generatePortraitComposite(
    templateBase64: string,
    url: string,
    serialNumber: string
  ): Promise<CompositeResult> {
    return this.generateComposite(
      templateBase64,
      url,
      serialNumber,
      PORTRAIT_CONFIG
    );
  }

  /**
   * Generate composite image for landscape orientation
   */
  static async generateLandscapeComposite(
    templateBase64: string,
    url: string,
    serialNumber: string
  ): Promise<CompositeResult> {
    return this.generateComposite(
      templateBase64,
      url,
      serialNumber,
      LANDSCAPE_CONFIG
    );
  }

  /**
   * Generate high-res composite for production download
   */
  static async generateHiResComposite(
    templateBase64: string,
    url: string,
    serialNumber: string,
    orientation: 'portrait' | 'landscape'
  ): Promise<CompositeResult> {
    const config = orientation === 'portrait' ? PORTRAIT_CONFIG : LANDSCAPE_CONFIG;
    return this.generateComposite(
      templateBase64,
      url,
      serialNumber,
      config
    );
  }

  /**
   * Generate composite image with the specified configuration
   */
  static async generateComposite(
    templateBase64: string,
    url: string,
    serialNumber: string,
    config: CompositeConfig
  ): Promise<CompositeResult> {
    // Load Inter font
    await this.loadInterFont();

    // Create canvas
    const canvas = document.createElement('canvas');
    canvas.width = config.templateWidth;
    canvas.height = config.templateHeight;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }

    // Load template image
    const templateImage = await this.createTemplateImage(templateBase64);
    
    // Draw template
    ctx.drawImage(templateImage, 0, 0, config.templateWidth, config.templateHeight);
    
    // Generate QR code at the required size with serial number embedded
    const qrCodeCanvas = await QRGenerator.generateQRCodeWithSize(
      url,
      config.qrCodeWidth,
      config.qrCodeHeight,
      serialNumber
    );
    
    // Draw QR code at specified position (serial number is now embedded in QR code)
    ctx.drawImage(qrCodeCanvas, config.qrCodeX, config.qrCodeY);
    
    // Get data URL
    const dataUrl = canvas.toDataURL('image/png');
    
    return {
      canvas,
      dataUrl,
    };
  }

  /**
   * Convert canvas to PNG blob
   */
  static async canvasToPng(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create PNG blob from canvas'));
        }
      }, 'image/png');
    });
  }

  /**
   * Convert canvas to high-quality PNG blob for production download
   * Returns PNG data as blob (despite method name similarity to TIFF)
   */
  static async canvasToHighQualityPng(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create high-quality PNG blob from canvas'));
        }
      }, 'image/png', 1.0); // Maximum quality PNG
    });
  }

  /**
   * @deprecated Use canvasToHighQualityPng instead - this method returns PNG data, not TIFF
   * Convert canvas to high-quality PNG blob for production download
   */
  static async canvasToTiff(canvas: HTMLCanvasElement): Promise<Blob> {
    return this.canvasToHighQualityPng(canvas);
  }

  /**
   * Download composite image as PNG file
   */
  static async downloadCompositePng(
    canvas: HTMLCanvasElement,
    serialNumber: string,
    orientation: 'Portrait' | 'Landscape'
  ): Promise<void> {
    const blob = await this.canvasToPng(canvas);
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.download = `${serialNumber}-${orientation}.png`;
    link.href = url;
    link.click();
    
    // Cleanup
    URL.revokeObjectURL(url);
  }

  /**
   * Download composite image as high-res PNG file (production quality)
   */
  static async downloadCompositeTiff(
    canvas: HTMLCanvasElement,
    serialNumber: string,
    orientation: 'Portrait' | 'Landscape'
  ): Promise<void> {
    const blob = await this.canvasToHighQualityPng(canvas);
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.download = `${serialNumber}-${orientation}-Production.png`;
    link.href = url;
    link.click();
    
    // Cleanup
    URL.revokeObjectURL(url);
  }
}